import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { Loader2, Minus, PenLine, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select } from '@/components/ui/select';
import { buttonVariants } from '@/components/ui/button';
import { FrameWithPolygon } from '@/components/ui/frame-with-polygon';
import { getLatestFrame } from '@/api/frames';
import { cn } from '@/lib/utils';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useAppState } from '../app/appContext';
import { useT } from '@/contexts/I18nContext';
import { useCan } from '@/hooks/useCan';
import { refreshZoneFeatures } from '@/hooks/useZoneFeatures';
import { extractApiError } from '@/utils/apiError';
import {
  getQueuesStaffSettings,
  patchQueuesStaffSettings,
  queuesStaffKey,
  type ArrivalAreaDto,
  type QueuesStaffPatch,
  type QueuesStaffSettingsDto,
} from '@/api/queuesStaff';

// «Очереди и сотрудники» (Алексей 22.10, #1798): порог алерта очереди, приход к
// открытию (вкл/выкл, за сколько минут, сколько человек), утренние сообщения и их
// чат. Время прихода раньше жило в часах работы — «это там не к месту».
// 25.10 #2321: «покрасивее — странные поля ввода, подписи где попало, отступы»; приход —
// одной фразой «на месте за N мин до открытия, не меньше N чел. в зоне „…“» с выбором зоны,
// кадром камеры и ссылкой «Разметить новую» в зонирование с подсказкой.

export default function QueuesStaffSettings() {
  const { zone } = useAppState();
  const zoneId = zone.selectedZoneId;
  if (!zoneId) return <BranchRequiredState />;
  return <QueuesStaffBody zoneId={zoneId} />;
}

function QueuesStaffBody({ zoneId }: { zoneId: string }) {
  const { t } = useT();
  const canEdit = useCan('zone.config.edit', zoneId);
  const { data, isLoading, mutate } = useSWR(queuesStaffKey(zoneId), () =>
    getQueuesStaffSettings(zoneId).then((r) => r.data),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(patch: QueuesStaffPatch) {
    setError(null);
    setBusy(true);
    try {
      const next = (await patchQueuesStaffSettings(zoneId, patch)).data;
      await mutate(next, { revalidate: false });
      if ('arrival_enabled' in patch) refreshZoneFeatures(zoneId);
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t('common.loading')}
      </div>
    );
  }
  const disabled = !canEdit || busy;

  return (
    <div className="space-y-4">
      <QueuesCard data={data} disabled={disabled} onSave={save} />
      <ArrivalCard data={data} disabled={disabled} busy={busy} onSave={save} zoneId={zoneId} />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── Очереди ──────────────────────────────────────────────────────────────

function QueuesCard({ data, disabled, onSave }: {
  data: QueuesStaffSettingsDto;
  disabled: boolean;
  onSave: (p: QueuesStaffPatch) => Promise<void>;
}) {
  const { t } = useT();
  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{t('queuesStaff.queues.title')}</CardTitle>
        <CardDescription>{t('queuesStaff.queues.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {data.queues.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('queuesStaff.queues.empty')}{' '}
            <Link to="/management/zoning" className="text-primary hover:underline">{t('queuesStaff.queues.emptyLink')}</Link>
          </p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {data.queues.map((q) => (
              <div key={q.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm font-medium">{q.name || t('queuesStaff.queues.unnamed')}</span>
                {/* «от N чел.» — как в калибровке: алерт, когда людей больше max_allowed_size. */}
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {t('queuesStaff.queues.alertFrom')}
                  <Stepper value={q.max_allowed_size + 1} min={1} max={51} disabled={disabled}
                    onCommit={(n) => onSave({ queues: [{ id: q.id, max_allowed_size: n - 1 }] })} />
                  {t('queuesStaff.people')}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Приход к открытию ────────────────────────────────────────────────────

function ArrivalCard({ data, disabled, busy, onSave, zoneId }: {
  data: QueuesStaffSettingsDto;
  disabled: boolean;
  busy: boolean;
  onSave: (p: QueuesStaffPatch) => Promise<void>;
  zoneId: string;
}) {
  const { t } = useT();
  const blocked = data.arrival_requirement === 'staff_polygon' && !data.arrival_enabled;
  const areas = data.arrival_areas ?? [];   // старый бэкенд без выбора зоны
  const staff = areas.filter((a) => a.kind === 'staff');
  const chosen = areas.find((a) => a.id === data.arrival_area_id) ?? null;
  // Превью: выбранная зона, иначе все рабочие зоны первой камеры с ними.
  const shown = chosen ? [chosen] : staff.filter((a) => a.camera_id === staff[0]?.camera_id);
  const markupLink = `/management/zoning?zone=${zoneId}&hint=arrival`;
  return (
    <Card variant="section">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{t('queuesStaff.arrival.title')}</CardTitle>
          <CardDescription>{t('queuesStaff.arrival.description')}</CardDescription>
          {blocked && <p className="text-xs text-amber-700 dark:text-amber-300">{t('queuesStaff.arrival.needsBar')}</p>}
        </div>
        <span className="flex shrink-0 items-center gap-2 pt-0.5">
          {busy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
          <Switch id="arrival-enabled" aria-label={t('queuesStaff.arrival.enable')} checked={data.arrival_enabled}
            disabled={disabled || blocked} onCheckedChange={(v) => onSave({ arrival_enabled: v })} />
        </span>
      </CardHeader>
      {data.arrival_enabled && (
        <CardContent className="space-y-5">
          {/* Правило одной фразой — как его и говорят: «на месте за 30 мин, не меньше 1 чел. в зоне „Бар“». */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5 text-sm">
            <span>{t('queuesStaff.arrival.minutesPrefix')}</span>
            <Stepper value={data.arrival_minutes} min={0} max={180} step={5} disabled={disabled}
              onCommit={(n) => onSave({ arrival_minutes: n })} />
            <span>{t('queuesStaff.arrival.minutesSuffix')},</span>
            <span>{t('queuesStaff.arrival.peoplePrefix')}</span>
            <Stepper value={data.arrival_people} min={1} max={10} disabled={disabled}
              onCommit={(n) => onSave({ arrival_people: n })} />
            <span>{t('queuesStaff.people')} {t('queuesStaff.arrival.inZone')}</span>
            <Select wrapperClassName="w-auto max-w-full" className="min-w-48" value={data.arrival_area_id ?? ''} disabled={disabled}
              onChange={(e) => onSave({ arrival_area_id: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{t('queuesStaff.arrival.allStaffZones')}</option>
              {areas.map((a) => (
                <option key={a.id} value={a.id}>{areaLabel(a, t)}</option>
              ))}
            </Select>
          </div>
          <p className="-mt-2 text-xs text-muted-foreground">{t('queuesStaff.arrival.captureNote')}</p>

          <div className="grid gap-3 sm:grid-cols-[minmax(0,26rem)_1fr] sm:items-start">
            <ZonePreview areas={shown} />
            <div className="space-y-2 text-sm">
              <div className="font-medium">{chosen ? chosen.name : t('queuesStaff.arrival.allStaffZones')}</div>
              <div className="text-xs text-muted-foreground">
                {shown[0] ? shown[0].camera_name : t('queuesStaff.arrival.noZone')}
              </div>
              <Link to={markupLink} className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'mt-1 gap-1.5')}>
                <PenLine className="size-4" />{t('queuesStaff.arrival.drawNew')}
              </Link>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <ToggleRow
              id="arrival-messages"
              label={t('queuesStaff.arrival.messages')}
              hint={data.arrival_messages ? t('queuesStaff.arrival.messagesOn') : t('queuesStaff.arrival.messagesOff')}
              checked={data.arrival_messages}
              disabled={disabled}
              busy={false}
              onChange={(v) => onSave({ arrival_messages: v })}
            />
            {data.arrival_messages && <div className="mt-3"><ArrivalChat data={data} disabled={disabled} onSave={onSave} /></div>}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function areaLabel(a: ArrivalAreaDto, t: (k: string) => string): string {
  return `${a.name} · ${t(a.kind === 'staff' ? 'zoning.types.staff' : 'zoning.types.standalone')} · ${a.camera_name.replace(/^.*?,\s*/, '')}`;
}

/** Кадр камеры с обведённой зоной — видно, где именно ждём людей. */
function ZonePreview({ areas }: { areas: ArrivalAreaDto[] }) {
  const { t } = useT();
  const cam = areas[0]?.camera_id;
  const { data: src, error } = useSWR(cam ? ['latest-frame', cam] : null, () => getLatestFrame(String(cam)).then((r) => r.data.image), { revalidateOnFocus: false });
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-border bg-muted">
      {!cam ? (
        <div className="flex h-full items-center justify-center p-4 text-center text-xs text-muted-foreground">{t('queuesStaff.arrival.noZone')}</div>
      ) : src && !error ? (
        <FrameWithPolygon src={src} polygons={areas.map((a) => a.vectors)} alt={areas[0].name} />
      ) : error ? (
        <div className="flex h-full items-center justify-center p-4 text-xs text-muted-foreground">{t('queuesStaff.arrival.noFrame')}</div>
      ) : (
        <div className="flex h-full items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
      )}
    </div>
  );
}

function ArrivalChat({ data, disabled, onSave }: {
  data: QueuesStaffSettingsDto;
  disabled: boolean;
  onSave: (p: QueuesStaffPatch) => Promise<void>;
}) {
  const { t } = useT();
  const [chatId, setChatId] = useState(data.arrival_chat_id ?? '');
  const [threadId, setThreadId] = useState(data.arrival_thread_id ?? '');
  useEffect(() => { setChatId(data.arrival_chat_id ?? ''); setThreadId(data.arrival_thread_id ?? ''); },
    [data.arrival_chat_id, data.arrival_thread_id]);

  function commit() {
    const c = chatId.trim() || null;
    const th = threadId.trim() || null;
    if (c === data.arrival_chat_id && th === data.arrival_thread_id) return;
    void onSave({ arrival_chat_id: c, arrival_thread_id: th });
  }

  return (
    <div className="grid gap-3 rounded-lg bg-muted/40 p-4 sm:grid-cols-2">
      <div className="grid gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">{t('queuesStaff.arrival.chatId')}</Label>
        <Input className="font-mono text-sm" value={chatId} onChange={(e) => setChatId(e.target.value)}
          onBlur={commit} disabled={disabled} placeholder="-100123456789" />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs font-medium text-muted-foreground">{t('queuesStaff.arrival.threadId')}</Label>
        <Input className="font-mono text-sm" value={threadId} onChange={(e) => setThreadId(e.target.value)}
          onBlur={commit} disabled={disabled} placeholder="1234" />
      </div>
      <p className="text-xs text-muted-foreground sm:col-span-2">{t('queuesStaff.arrival.chatHint')}</p>
    </div>
  );
}

// ── Мелкие поля ──────────────────────────────────────────────────────────

function ToggleRow({ id, label, hint, checked, disabled, busy, onChange }: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  disabled: boolean;
  busy: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex items-center gap-2">
        {busy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
        <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
      </div>
    </div>
  );
}

/** Число кнопками −/+ и вводом; сохраняется по уходу из поля или по кнопке; вне границ — прижимается. */
function Stepper({ value, min, max, step = 1, disabled, onCommit }: {
  value: number;
  min: number;
  max: number;
  step?: number;
  disabled: boolean;
  onCommit: (n: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  function commit(n: number) {
    if (!Number.isFinite(n)) { setText(String(value)); return; }
    const v = clamp(n);
    setText(String(v));
    if (v !== value) onCommit(v);
  }
  const btn = 'flex size-8 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40';
  return (
    <span className="inline-flex h-9 items-center overflow-hidden rounded-md border border-input bg-background shadow-sm">
      <button type="button" className={btn} disabled={disabled || value <= min} onClick={() => commit(value - step)} aria-label="−"><Minus className="size-3.5" /></button>
      <input type="text" inputMode="numeric" value={text} disabled={disabled}
        onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))} onBlur={() => commit(Number(text))}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="h-full w-10 border-x border-input bg-transparent text-center text-sm font-medium tabular-nums text-foreground outline-none focus:bg-muted/40" />
      <button type="button" className={btn} disabled={disabled || value >= max} onClick={() => commit(value + step)} aria-label="+"><Plus className="size-3.5" /></button>
    </span>
  );
}
